import { PaymentMethod } from '@stripe/stripe-js';

import { FlagCode } from '../components/flag/index';
import { StringKey } from '../lang/index';
import { Country } from './country.types';
import { Currency } from './currency.types';
import { Plan, SubscriptionType } from './plan.types';
import { Stakeholder, StakeholderRole } from './stakeholder.types';
import { State } from './state.types';

export enum CompanyModule {
  CAP_TABLE = 'cap-table',
  DASHBOARD = 'dashboard',
  DIVIDENDS = 'dividends',
  EVENTS = 'events',
  MY_HOLDINGS = 'my-holdings',
  NOTIFICATIONS = 'notifications',
  POOLS_AND_PLANS = 'pools-and-plans',
  SAFES = 'safes',
  SCENARIO_MODELING = 'scenario-modeling',
  SHARE_CLASSES = 'share-classes',
  STAKEHOLDER = 'stakeholder',
  STAKEHOLDER_VAULT = 'stakeholder-vault',
  VAULT = 'vault',
}

export enum CompanyIndustry {
  ACCOUNTING = 'accounting',
  AD_TECH = 'ad-tech',
  AEROSPACE = 'aerospace',
  AGRICULTURE_FARMING = 'agriculture-farming',
  ART = 'art',
  ARTIFICIAL_INTELLIGENCE = 'artificial-intelligence',
  AUDIT = 'audit',
  AUTOMOTIVE = 'automotive',
  AVIATION = 'aviation',
  BANKING_FINANCIAL_SERVICES = 'banking-financial-services',
  BIOTECHNOLOGY = 'biotechnology',
  BUILDING_MATERIALS_SUPPLIES = 'building-materials-supplies',
  BUSINESS_SERVICES = 'business-services',
  CHEMICAL_INDUSTRY = 'chemical-industry',
  CLEANING_PRODUCTS_SERVICES = 'cleaning-products-services',
  CLOUD_COMPUTING = 'cloud-computing',
  COMPUTER_HARDWARE = 'computer-hardware',
  CONSTRUCTION = 'construction',
  CONSULTING = 'consulting',
  CONSUMER_ELECTRONICS = 'consumer-electronics',
  CONSUMER_GOODS = 'consumer-goods',
  CONSUMER_HEALTHCARE = 'consumer-healthcare',
  CULTURE = 'culture',
  CYBERSECURITY = 'cybersecurity',
  DATA_ANALYTICS = 'data-analytics',
  DATA_STORAGE_MANAGEMENT = 'data-storage-management',
  DEFENSE = 'defense',
  E_COMMERCE = 'e-commerce',
  ED_TECH = 'ed-tech',
  EDUCATION_TRAINING = 'education-training',
  ELECTRIC_VEHICLES = 'electric-vehicles',
  ENERGY = 'energy',
  ENTERTAINMENT = 'entertainment',
  ENVIRONMENTAL_SERVICES = 'environmental-services',
  ESPORTS = 'esports',
  EVENTS = 'events',
  FABRICS_TEXTILES = 'fabrics-textiles',
  FASHION = 'fashion',
  FINTECH = 'fintech',
  FOOD_BEVERAGES = 'food-beverages',
  GOVERNMENT_PUBLIC_INSTITUTIONS = 'government-public-institutions',
  HEALTHCARE = 'healthcare',
  HOSPITALITY = 'hospitality',
  HR_TECH = 'hr-tech',
  IGAMING = 'igaming',
  INFORMATION_TECHNOLOGIES = 'information-technologies',
  INSURANCE = 'insurance',
  INTERNET_OF_THINGS = 'internet-of-things',
  LEGAL = 'legal',
  LEGAL_COMPLIANCE = 'legal-compliance',
  LEISURE_RECREATION = 'leisure-recreation',
  LOGISTICS_SUPPLY_CHAIN = 'logistics-supply-chain',
  LUXURY_GOODS = 'luxury-goods',
  MACHINERY_HEAVY_EQUIPMENT = 'machinery-heavy-equipment',
  MANUFACTURING = 'manufacturing',
  MARKETING_ADVERTISING = 'marketing-advertising',
  MEDIA = 'media',
  MEDICAL_DEVICES_SUPPLIES = 'medical-devices-supplies',
  MINING = 'mining',
  MOBILITY_TRANSPORTATION = 'mobility-transportation',
  MUSIC = 'music',
  NON_PROFIT = 'non-profit',
  OTHER = 'other',
  PERSONAL_SERVICES = 'personal-services',
  PET_CARE_SUPPLIES = 'pet-care-supplies',
  PHARMACEUTICALS = 'pharmaceuticals',
  PROFESSIONAL_SERVICES = 'professional-services',
  PUBLISHING = 'publishing',
  REAL_ESTATE_PROPTECH = 'real-estate-proptech',
  RESTAURANTS_FOOD_SERVICES = 'restaurants-food-services',
  RETAIL = 'retail',
  ROBOTICS = 'robotics',
  SMART_CITY = 'smart-city',
  SOCIAL_MEDIA = 'social-media',
  SOFTWARE_DEVELOPMENT = 'software-development',
  SPORTS_FITNESS = 'sports-fitness',
  TAX = 'tax',
  TELECOM_CONNECTIVITY = 'telecom-connectivity',
  TRAVEL_TOURISM = 'travel-tourism',
  VIDEO_GAMES = 'video-games',
  WASTE_MANAGEMENT = 'waste-management',
}
export enum CompanyType {
  COMPANY = 'company',
  FAMILY_OFFICE = 'family-office',
  OTHER = 'other',
  PRIVATE_EQUITY = 'private-equity',
  SPV = 'spv',
  VENTURE_CAPITAL = 'venture-capital',
}
export enum CompanyAnnualRevenue {
  BETWEEN_1_5_MIL = 'between-1-5-mil',
  BETWEEN_5_20_MIL = 'between-5-20-mil',
  LESS_1_MIL = 'less-1-mil',
  MORE_20_MIL = 'more-20-mil',
  NO_REV = 'no-rev',
}

export enum CompanyNumberOfEmployees {
  BETWEEN_1_10 = '1-10',
  BETWEEN_11_25 = '11-25',
  BETWEEN_26_50 = '26-50',
  BETWEEN_51_250 = '51-250',
  MORE_250 = '>250',
}

export enum IncorporatedIn {
  ABU_DHABI_AIRPORT_FREE_ZONE = 'ABU_DHABI_AIRPORT_FREE_ZONE',
  ADGM = 'ADGM',
  AJMAN_FREE_ZONE = 'AJMAN_FREE_ZONE',
  AJMAN_MEDIA_CITY_FREE_ZONE = 'AJMAN_MEDIA_CITY_FREE_ZONE',
  D3 = 'D3',
  DAFZA = 'DAFZA',
  DHCC = 'DHCC',
  DIAC = 'DIAC',
  DIC = 'DIC',
  DIFC = 'DIFC',
  DKP = 'DKP',
  DMC = 'DMC',
  DMCC = 'DMCC',
  DSO = 'DSO',
  DSP = 'DSP',
  DUBAI_AUTO_ZONE = 'DUBAI_AUTO_ZONE',
  DUBAI_COMMERCITY = 'DUBAI_COMMERCITY',
  DUBAI_FLOWER_CENTRE = 'DUBAI_FLOWER_CENTRE',
  DUBAI_GOLD_AND_DIAMOND_PARK = 'DUBAI_GOLD_AND_DIAMOND_PARK',
  DUBAI_INDUSTRIAL_CITY = 'DUBAI_INDUSTRIAL_CITY',
  DUBAI_LOGISTICS_CITY = 'DUBAI_LOGISTICS_CITY',
  DUBAI_MARITIME_CITY = 'DUBAI_MARITIME_CITY',
  DUBAI_OUTSOURCE_CITY = 'DUBAI_OUTSOURCE_CITY',
  DUBAI_PRODUCTION_CITY = 'DUBAI_PRODUCTION_CITY',
  DUBAI_SOUTH = 'DUBAI_SOUTH',
  DUBAI_STUDIO_CITY = 'DUBAI_STUDIO_CITY',
  DUBAI_TECHNO_PARK = 'DUBAI_TECHNO_PARK',
  DUBAI_TEXTILE_CITY = 'DUBAI_TEXTILE_CITY',
  DUBAI_WHOLESALE_CITY = 'DUBAI_WHOLESALE_CITY',
  DUBIOTECH = 'DUBIOTECH',
  DUCAMZ = 'DUCAMZ',
  DWTCA = 'DWTCA',
  FOIZ = 'FOIZ',
  FUJAIRAH_CREATIVE_CITY = 'FUJAIRAH_CREATIVE_CITY',
  FUJAIRAH_FREE_ZONE = 'FUJAIRAH_FREE_ZONE',
  HAMRIYAH_FREE_ZONE = 'HAMRIYAH_FREE_ZONE',
  ICAD = 'ICAD',
  IHC = 'IHC',
  IMPZ = 'IMPZ',
  JAFZA = 'JAFZA',
  KIZAD = 'KIZAD',
  MASDAR_CITY_FREE_ZONE = 'MASDAR_CITY_FREE_ZONE',
  MEYDAN_FREE_ZONE = 'MEYDAN_FREE_ZONE',
  RAK_FREE_TRADE_ZONE = 'RAK_FREE_TRADE_ZONE',
  RAK_ICC = 'RAK_ICC',
  RAK_MARITIME_CITY_FREE_ZONE = 'RAK_MARITIME_CITY_FREE_ZONE',
  RAKEZ = 'RAKEZ',
  RAKIA_FREE_ZONE = 'RAKIA_FREE_ZONE',
  RAKMC = 'RAKMC',
  SAIF_ZONE = 'SAIF_ZONE',
  SHAMS = 'SHAMS',
  SPC = 'SPC',
  SRTIP = 'SRTIP',
  TWOFOUR54 = 'TWOFOUR54',
  UMM_AL_QUWAIN_FREE_TRADE_ZONE = 'UMM_AL_QUWAIN_FREE_TRADE_ZONE',
  ZONESCORP = 'ZONESCORP',
}

export enum IssuedSharesFormula {
  ISSUED_SHARES_AND_AVAILABLE_POOL = 'issued-shares-and-available-pool',
  STRICT_ISSUED_SHARES = 'strict-issued-shares',
}

export type IssuedSharesCardType = {
  description: StringKey;
  title: StringKey;
  tooltip: StringKey;
};

export const IssuedSharesFormulaTitle: Record<IssuedSharesFormula, IssuedSharesCardType> = {
  [IssuedSharesFormula.ISSUED_SHARES_AND_AVAILABLE_POOL]: {
    title: 'ISSUED_SHARES_AVAILABLE_POOL',
    description: 'INCLUDES_UNISSUED_SHARES_FROM_POOL',
    tooltip: 'ISSUED_SHARES_EXPLANATION',
  },
  [IssuedSharesFormula.STRICT_ISSUED_SHARES]: {
    title: 'STRICTLY_ISSUED_SHARES',
    description: 'EXCLUDES_UNISSUED_SHARES_FROM_POOL',
    tooltip: 'STRICTLY_ISSUED_SHARES_EXPLANATION',
  },
};

export const incorporatedInTitle: Record<IncorporatedIn, string> = {
  [IncorporatedIn.DIFC]: 'Dubai International Financial Centre (DIFC)',
  [IncorporatedIn.DMCC]: 'Dubai Multi Commodities Centre (DMCC)',
  [IncorporatedIn.DIC]: 'Dubai Internet City (DIC)',
  [IncorporatedIn.DMC]: 'Dubai Media City (DMC)',
  [IncorporatedIn.DHCC]: 'Dubai Healthcare City (DHCC)',
  [IncorporatedIn.DKP]: 'Dubai Knowledge Park (DKP)',
  [IncorporatedIn.DSO]: 'Dubai Silicon Oasis (DSO)',
  [IncorporatedIn.JAFZA]: 'Jebel Ali Free Zone (JAFZA)',
  [IncorporatedIn.ADGM]: 'Abu Dhabi Global Markets (ADGM)',
  [IncorporatedIn.KIZAD]: 'Khalifa Industrial Zone Abu Dhabi (KIZAD)',
  [IncorporatedIn.ABU_DHABI_AIRPORT_FREE_ZONE]: 'Abu Dhabi Airport Free Zone (ADAFZ)',
  [IncorporatedIn.TWOFOUR54]: 'Twofour54 (Media Free Zone)',
  [IncorporatedIn.SAIF_ZONE]: 'Sharjah Airport International Free Zone (SAIF Zone)',
  [IncorporatedIn.SHAMS]: 'Sharjah Media City (SHAMS)',
  [IncorporatedIn.RAKEZ]: 'Ras Al Khaimah Economic Zone (RAKEZ)',
  [IncorporatedIn.RAK_MARITIME_CITY_FREE_ZONE]: 'RAK Maritime City Free Zone',
  [IncorporatedIn.RAK_FREE_TRADE_ZONE]: 'RAK Free Trade Zone',
  [IncorporatedIn.AJMAN_FREE_ZONE]: 'Ajman Free Zone (AFZ)',
  [IncorporatedIn.FUJAIRAH_FREE_ZONE]: 'Fujairah Free Zone (FFZ)',
  [IncorporatedIn.UMM_AL_QUWAIN_FREE_TRADE_ZONE]: 'Umm Al Quwain Free Trade Zone (UAQ FTZ)',
  [IncorporatedIn.DAFZA]: 'Dubai Airport Free Zone (DAFZA)',
  [IncorporatedIn.DUBAI_SOUTH]: 'Dubai South (formerly Dubai World Central)',
  [IncorporatedIn.D3]: 'Dubai Design District (d3)',
  [IncorporatedIn.DSP]: 'Dubai Science Park (DSP)',
  [IncorporatedIn.DUBAI_PRODUCTION_CITY]: 'Dubai Production City (formerly IMPZ)',
  [IncorporatedIn.DUBAI_STUDIO_CITY]: 'Dubai Studio City',
  [IncorporatedIn.DUBAI_OUTSOURCE_CITY]: 'Dubai Outsource City',
  [IncorporatedIn.DIAC]: 'Dubai International Academic City (DIAC)',
  [IncorporatedIn.DUBAI_TECHNO_PARK]: 'Dubai Techno Park',
  [IncorporatedIn.DUBAI_AUTO_ZONE]: 'Dubai Auto Zone',
  [IncorporatedIn.DUBAI_FLOWER_CENTRE]: 'Dubai Flower Centre',
  [IncorporatedIn.DUBAI_GOLD_AND_DIAMOND_PARK]: 'Dubai Gold and Diamond Park',
  [IncorporatedIn.DUBAI_INDUSTRIAL_CITY]: 'Dubai Industrial City',
  [IncorporatedIn.DUBAI_WHOLESALE_CITY]: 'Dubai Wholesale City (Dubai Industrial Park)',
  [IncorporatedIn.DWTCA]: 'Dubai World Trade Centre Authority (DWTCA)',
  [IncorporatedIn.DUBAI_LOGISTICS_CITY]: 'Dubai Logistics City (Dubai South Logistics District)',
  [IncorporatedIn.DUBIOTECH]: 'Dubai Biotechnology and Research Park (DuBiotech)',
  [IncorporatedIn.IHC]: 'International Humanitarian City (IHC)',
  [IncorporatedIn.IMPZ]: 'International Media Production Zone (IMPZ)',
  [IncorporatedIn.DUCAMZ]: 'Dubai Cars and Automotive Zone (DUCAMZ)',
  [IncorporatedIn.MEYDAN_FREE_ZONE]: 'Meydan Free Zone',
  [IncorporatedIn.DUBAI_COMMERCITY]: 'Dubai CommerCity',
  [IncorporatedIn.DUBAI_TEXTILE_CITY]: 'Dubai Textile City',
  [IncorporatedIn.DUBAI_MARITIME_CITY]: 'Dubai Maritime City',
  [IncorporatedIn.MASDAR_CITY_FREE_ZONE]: 'Masdar City Free Zone',
  [IncorporatedIn.ICAD]: 'Industrial City of Abu Dhabi (ICAD)',
  [IncorporatedIn.ZONESCORP]: 'ZonesCorp (Higher Corporation for Specialized Economic Zones)',
  [IncorporatedIn.HAMRIYAH_FREE_ZONE]: 'Hamriyah Free Zone',
  [IncorporatedIn.SPC]: 'Sharjah Publishing City (SPC)',
  [IncorporatedIn.SRTIP]: 'Sharjah Research Technology and Innovation Park (SRTIP)',
  [IncorporatedIn.AJMAN_MEDIA_CITY_FREE_ZONE]: 'Ajman Media City Free Zone',
  [IncorporatedIn.FUJAIRAH_CREATIVE_CITY]: 'Fujairah Creative City',
  [IncorporatedIn.FOIZ]: 'Fujairah Oil Industry Zone (FOIZ)',
  [IncorporatedIn.RAK_ICC]: 'RAK International Corporate Centre (RAK ICC)',
  [IncorporatedIn.RAKIA_FREE_ZONE]: 'RAK Investment Authority (RAKIA) Free Zone',
  [IncorporatedIn.RAKMC]: 'RAK Media City (RAKMC)',
};

export const incorporatedInTitleMap = Object.entries(incorporatedInTitle);

export const unitedArabEmiratesIso2: Lowercase<FlagCode> = 'ae';

export type PatchEnvelopeLimitDto = {
  docusignEnvelopeLimit: number;
};

export type PatchCompanyDto = {
  address?: string;
  annualRevenue?: CompanyAnnualRevenue;
  city?: string;
  companyId?: string;
  countryIso2?: string;
  coupon?: string;
  currencyCountryIso2Code?: string;
  currencyIso3?: string;
  incorporatedIn?: IncorporatedIn | null;
  incorporationZoneName?: string;
  industry?: CompanyIndustry;
  issuedSharesFormula?: IssuedSharesFormula;
  name?: string;
  numberOfEmployees?: CompanyNumberOfEmployees;
  planId?: string;
  stateCountryIso2?: string;
  stateIso2?: string;
  type?: CompanyType;
  url?: string;
  vatGst?: null | string;
  zipCode?: string;
};

export type LatestSubscriptionData = {
  current_period_end?: number;
  current_period_start?: number;
  id: string;
  items?: {
    data: { price: { product: { metadata: { title: string; type: SubscriptionType } } } }[];
  };
  plan?: {
    active: boolean;
    amount: number;
    created: number;
    currency: string;
    id: string;
    interval: string;
  };
  status?: SubscriptionStatus;
};

export type DocusignEnvelope = {
  hasOverride: boolean;
  limit: number;
  periodStart: null | string;
  remaining: number;
  used: number;
};

export type Company = {
  address?: string;
  annualRevenue?: CompanyAnnualRevenue;
  city?: string;

  companyId?: string;
  // location & logo
  country?: Country;
  createdAt: string;
  currency?: Currency;
  currencyCountryIso2Code?: string;

  docusignEnvelope?: DocusignEnvelope;
  id: string;
  inactivateAt?: string;
  incorporatedIn?: IncorporatedIn;
  incorporationZoneName?: string;
  industry?: CompanyIndustry;
  isActive: boolean;
  isCompleted: boolean;

  isEnterpriseRequested?: boolean;
  /** Narrows what the role means: a member who reaches only the data rooms shared with them. */
  isExternalGuest?: boolean;
  isFirstSubscribed: boolean;
  isSelected: boolean;
  issuedSharesFormula: IssuedSharesFormula;
  // Nullable in the DB (`Company.latestSubscriptionData` is a nullable jsonb): a company can exist
  // before its first Stripe subscription lands. Declaring it required made every `company?.x.y`
  // read compile while still throwing at runtime.
  latestSubscriptionData?: LatestSubscriptionData;
  logoImgSrc?: string;
  // company details
  name?: string;

  numberOfEmployees?: CompanyNumberOfEmployees;
  plan?: Plan;
  planId?: string;
  roleInCompany: StakeholderRole;
  stakeholder?: Stakeholder;
  stakeholdersLimit: number;
  state?: State;
  stripeSubscriptionId?: string;
  subscriptionOfferId?: null | string;
  toCancelAt?: string;
  type?: CompanyType;
  updatedAt: string;
  url?: string;
  vatGst?: string;
  zipCode?: string;
};

export enum CompanyFilter {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}
export type FilterVariant = 'all' | CompanyFilter;

export const filterVariant: Record<FilterVariant, StringKey> = {
  all: 'FILTER_ALL',
  [CompanyFilter.ACTIVE]: 'FILTER_ACTIVE',
  [CompanyFilter.INACTIVE]: 'FILTER_INACTIVE',
};

export const filterVariantMap = Object.entries(filterVariant);

export type UpgradeCompanyDto = {
  planId: string;
};

export type GetStartedProgress = {
  isCompleteCompanyIncorporation: boolean;
  isCompleteEvent: boolean;
  isCompletePlan: boolean;
  isCompletePool: boolean;
  isCompleteShareClass: boolean;
  isCompleteStakeholders: boolean;
};
export interface PaymentData {
  defaultPaymentMethod: PaymentMethod;
  periodEnd: number;
}

export type Invoice = {
  amount_due: number;
  amount_paid: number;
  created: number;
  currency: string;
  hosted_invoice_url?: null | string;
  id: string;
  invoice_pdf: string;
  lines: {
    data: {
      plan?: {
        interval: string;
      };
    }[];
  };
  paid: boolean;
};

export enum SubscriptionStatus {
  ACTIVE = 'active',
  CANCELED = 'canceled',
  INCOMPLETE = 'incomplete',
  INCOMPLETE_EXPIRED = 'incomplete_expired',
  PAST_DUE = 'past_due',
  TRIALING = 'trialing',
  UNPAID = 'unpaid',
}
